import { lensProp, adjust, over, append, identity } from 'ramda';
import { ISectionData } from '../KolumnValjare';
import {
  getColumnFields,
  safeGetAttributeInfo,
} from '../../../Model/avenyModelUtils';
import { IColumnDef } from '../../../Types/ColumnDefType';
import { IClientAssociationModel } from '../../../Types/ClientModelTypes';
import { AvenyCollectionMap } from '../../../Model/ModelContext/ModelContext';
import mergeSort, { stringCompareNumeric } from '../../../Utils/mergeSort';
import { ITreeSection } from '../../../BaseComponents/TreeSelect/TreeSelectUtils';

const getPickedColumn = (pickedColumns: ITreeSection<ISectionData>[]) => {
  return (id: string): ITreeSection<ISectionData> | undefined => {
    return pickedColumns.find(x => x.value === id);
  };
};

const subListLens = lensProp('subList');

export const columnsCompareFn = (a: ITreeSection, b: ITreeSection): number =>
  stringCompareNumeric(a.name, b.name);

const getNamePath = (
  integratedPath: IClientAssociationModel[] | undefined,
  name: string
): string[] => {
  if (integratedPath) {
    return append(
      name,
      integratedPath.map(pathElement => pathElement.AssociationName)
    );
  } else {
    return [name];
  }
};

export const getUnpickedColumns = (
  collectionMap: AvenyCollectionMap,
  pickedColumns: ITreeSection<ISectionData>[],
  collectionName: string,
  componentName: string | null,
  parentViewPath: string = '',
  parentModelPath: string = ''
): ITreeSection<ISectionData>[] => {
  const pickedColumn = getPickedColumn(pickedColumns);
  const { attributeIntsWithGroup, attributeIntsWithoutGroup, associationInts } =
    getColumnFields(collectionName, componentName, collectionMap);

  let groups: ITreeSection<ISectionData>[] = [];
  for (let i = 0; i < attributeIntsWithGroup.length; i++) {
    const attrInt = attributeIntsWithGroup[i];
    const attr = attrInt.attribute;
    const namePath = getNamePath(attrInt.integratedIn, attr.AttributeName);
    const id = parentModelPath + namePath.join('.');

    const groupIndex = groups.findIndex(
      group => group.value === parentModelPath + attr.AttributeGroup
    );

    const viewPath = namePath[namePath.length - 1];

    const pickedColumnValue = pickedColumn(id);
    const isHeader = pickedColumnValue?.data?.header;
    const disabled = !!pickedColumnValue && isHeader;
    const column: ITreeSection<ISectionData> = {
      name: attr.DisplayLabel,
      value: id,
      id: id,
      checked: !!pickedColumnValue,
      disabled: disabled
        ? {
            value: disabled,
            tooltip: '',
          }
        : undefined,
      data: {
        fieldType: 'attribute',
        path: parentViewPath + attr.AttributeGroup + '.' + viewPath,
        isAttributeGroup: false,
        isExpandable: false,
        header: isHeader,
      },
    };
    if (groupIndex === -1) {
      groups.push({
        name: attr.AttributeGroup ?? '',
        value: parentModelPath + attr.AttributeGroup,
        id: parentModelPath + attr.AttributeGroup,
        data: {
          fieldType: 'expandable',
          associatedCollectionName: collectionName,
          associatedComponentName: collectionName,
          isExpandable: true,
          isAttributeGroup: true,
          path: `&association-${parentViewPath}${attr.AttributeGroup}`,
        },
        subList: [column],
      });
    } else {
      groups = adjust(groupIndex, over(subListLens, append(column)), groups);
    }
  }

  // Sort groups
  groups.forEach(group => {
    group.subList = mergeSort(group.subList ?? [], columnsCompareFn);
  });

  const associationColumns: ITreeSection<ISectionData>[] = associationInts.map(
    associationInt => {
      const association = associationInt.association;
      const namePath = getNamePath(
        associationInt.integratedIn,
        association.AssociationName
      );
      const viewPath = namePath[namePath.length - 1];

      return {
        name: association.DisplayLabel,
        value: parentModelPath + namePath.join('.'),
        id: parentModelPath + namePath.join('.'),
        data: {
          fieldType: 'expandable',
          path: `&association-${parentViewPath}${viewPath}`,
          isExpandable: true,
          isAttributeGroup: false,
          associatedCollectionName: association.AssociatedCollectionName,
          associatedComponentName: association.AssociatedComponentName,
        },
      };
    }
  );

  let columns: ITreeSection<ISectionData>[] = [];
  attributeIntsWithoutGroup.forEach(attributeInt => {
    const attribute = attributeInt.attribute;
    const namePath = getNamePath(
      attributeInt.integratedIn,
      attribute.AttributeName
    );
    const id = parentModelPath + namePath.join('.');

    const viewPath = namePath[namePath.length - 1];

    const pickedColumnValue = pickedColumn(id);
    const disabled = !!pickedColumnValue && pickedColumnValue.data?.header;
    columns.push({
      name: attribute.DisplayLabel,
      value: id,
      id: id,
      checked: !!pickedColumnValue,
      disabled: disabled
        ? {
            value: disabled,
            tooltip: '',
          }
        : undefined,
      data: {
        fieldType: 'attribute',
        path: parentViewPath + viewPath,
        isAttributeGroup: false,
        isExpandable: false,
        header: pickedColumnValue?.data?.header,
      },
    });
  });

  const allColumns: ITreeSection[] = columns
    .concat(associationColumns)
    .concat(groups);
  const allColumnsSorted = mergeSort(allColumns, columnsCompareFn);

  return allColumnsSorted;
};

export const getPickedColumns = (
  valdaKolumner: readonly IColumnDef[],
  collectionName: string,
  collectionMap: AvenyCollectionMap
): ITreeSection<ISectionData>[] => {
  return valdaKolumner.map(valdKolumn => {
    const attributeAndViewPath = safeGetAttributeInfo(
      collectionName,
      valdKolumn.attributeDef,
      collectionMap
    );

    // Om caption inte är samma som i modellen så ska caption sparas som ownName
    const modelDisplayLabel = attributeAndViewPath.mapOrElse(
      identity,
      x => x.attribute.DisplayLabel
    );
    const ownName = valdKolumn.caption;
    const hasOwnName = ownName !== modelDisplayLabel;

    return {
      name: modelDisplayLabel,
      value: valdKolumn.attributeDef,
      id: valdKolumn.attributeDef,
      data: {
        fieldType: 'attribute',
        path: valdKolumn.path
          ? valdKolumn.path
          : attributeAndViewPath.mapOr('', x => x.viewPath),
        isExpandable: false,
        isAttributeGroup: false,
        ownName: hasOwnName ? ownName : undefined,
        header: valdKolumn.header,
      },
    };
  });
};
